import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, MousePointerClick, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "@/lib/api";
import { CATEGORY_LABELS } from "@/components/result/PartnersBox";
import { PartnerForm, PLATFORMS } from "@/components/admin/PartnerForm";

const PERIODS = [["7d", "Ultimi 7 giorni"], ["test", "Dal lancio del test"], ["all", "Totale"]];
const card = "rounded-3xl border border-emerald-100 bg-white p-6";

const Clicks = () => {
  const [period, setPeriod] = useState("7d");
  const [c, setC] = useState(null);
  useEffect(() => { adminApi.affiliateClicks(period).then(setC).catch(() => null); }, [period]);
  return (
    <div data-testid="affiliate-clicks" className={card}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-display text-lg font-semibold"><MousePointerClick className="h-5 w-5 text-emerald-600" />Clic sui link affiliati
          <span data-testid="affiliate-clicks-total" className="rounded-full bg-emerald-50 px-3 py-0.5 text-sm text-emerald-700">{c?.total ?? 0}</span></p>
        <div className="flex gap-2">{PERIODS.map(([k, l]) => (
          <button key={k} data-testid={`affiliate-period-${k}`} onClick={() => setPeriod(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${period === k ? "bg-emerald-600 text-white" : "bg-slate-50 text-gray-600 hover:bg-emerald-50"}`}>{l}</button>))}</div>
      </div>
      <div className="mt-5 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">Per partner</p>
          <table data-testid="affiliate-clicks-table" className="mt-2 w-full text-left text-sm">
            <tbody>{(c?.by_partner || []).map((r) => (
              <tr key={r.target} className="border-t border-gray-100"><td className="py-2 font-semibold">{r.name}</td><td className="text-gray-500">{PLATFORMS[r.platform] || r.platform}</td>
                <td className="text-gray-500">{CATEGORY_LABELS[r.category] || r.category}</td><td className="text-right font-semibold">{r.clicks}</td>
                <td className="pl-3 text-right text-xs text-gray-500">affiliati: {r.affiliate}</td></tr>))}
              {!c?.by_partner?.length && <tr><td className="py-3 text-gray-500">Nessun clic nel periodo</td></tr>}</tbody>
          </table>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">Top destinazioni</p>
          <ul className="mt-2 space-y-1 text-sm">{(c?.by_destination || []).map((r) => (
            <li key={r.destination} className="flex justify-between"><span>{r.destination}</span><span className="font-semibold">{r.clicks}</span></li>))}</ul>
          <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-gray-500">Per giorno</p>
          <ul className="mt-2 space-y-1 text-sm">{(c?.by_day || []).slice(-7).map((r) => (
            <li key={r.day} className="flex justify-between"><span>{r.day}</span><span className="font-semibold">{r.clicks}</span></li>))}</ul>
        </div>
      </div>
    </div>
  );
};

export const AffiliatesPanel = () => {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => adminApi.partners().then(setList).catch(() => null), []);
  useEffect(() => { load(); }, [load]);
  const save = async (body) => {
    try {
      if (edit?.id) await adminApi.updatePartner(edit.id, body); else await adminApi.createPartner(body);
      toast.success("Partner salvato");
      setEdit(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Eliminare definitivamente "${p.name}"?`)) return;
    try { await adminApi.deletePartner(p.id); toast.success("Partner eliminato"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (p) => {
    const { id, created_at, updated_at, ...body } = p;
    try { await adminApi.updatePartner(id, { ...body, active: !p.active }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="affiliates-panel" className="space-y-6">
      <Clicks />
      <div className={card}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-display text-lg font-semibold">Partner</p>
            <p className="text-sm text-gray-500">Booking.com passa da CJ (quando inserisci i dati CJ), Skyscanner da Travelpayouts se supportato. I partner non influenzano il TS Impact Score.</p>
          </div>
          <button data-testid="partner-add-button" onClick={() => setEdit({})}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-500"><Plus className="h-4 w-4" />Aggiungi partner</button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table data-testid="partners-table" className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-widest text-gray-500"><tr><th className="py-2">Nome</th><th>Piattaforma</th><th>Categoria</th><th>Destinazioni</th><th>Attivo</th><th /></tr></thead>
            <tbody>{list.map((p) => (
              <tr key={p.id} data-testid={`partner-row-${p.id}`} className="border-t border-gray-100">
                <td className="py-3 font-semibold">{p.name}</td><td>{PLATFORMS[p.platform]}</td><td>{CATEGORY_LABELS[p.category]}</td>
                <td className="max-w-[220px] truncate text-gray-600">{p.destinations?.length ? p.destinations.join(", ") : "Tutte"}</td>
                <td><button data-testid={`partner-toggle-${p.id}`} onClick={() => toggle(p)} aria-pressed={p.active} title={p.active ? "Attivo: clic per disattivare" : "Disattivato: clic per attivare"}
                  className={`relative h-6 w-11 rounded-full transition-colors ${p.active ? "bg-emerald-500" : "bg-gray-300"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${p.active ? "translate-x-5" : "translate-x-0.5"}`} /></button></td>
                <td className="whitespace-nowrap text-right"><button data-testid={`partner-edit-${p.id}`} onClick={() => setEdit(p)} className="rounded-full p-2 text-gray-500 hover:bg-emerald-50 hover:text-emerald-700" aria-label="Modifica"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`partner-delete-${p.id}`} onClick={() => remove(p)} className="rounded-full p-2 text-gray-500 hover:bg-red-50 hover:text-red-600" aria-label="Elimina"><Trash2 className="h-4 w-4" /></button></td>
              </tr>))}
              {!list.length && <tr><td className="py-4 text-gray-500" colSpan={6}>Nessun partner: aggiungi il primo.</td></tr>}</tbody>
          </table>
        </div>
      </div>
      {edit && <PartnerForm partner={edit.id ? edit : null} onClose={() => setEdit(null)} onSave={save} />}
    </div>
  );
};
